import React from 'react'
import styled from 'styled-components'
import { color, Media, mbSize } from '../../theme'
import { Img } from '../img'

const CardContainer = styled.div`
  padding: 25px;
  background: #ffefec;
  border-radius: 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: space-between;
  border: 1px solid #ffd6ce;

  ${Media.lessThan(Media.small)} {
    flex-direction: column-reverse;
    padding: ${mbSize(40)};
  }
`

const CardContent = styled.div`
  flex: 1;
  padding: 19px 0;
  max-width: 286px;
  ${Media.lessThan(Media.small)} {
    min-width: 100%;
    padding: 0;
    margin-top: ${mbSize(40)};
  }
`

const CardTitle = styled.span`
  font-size: 20px;
  font-weight: bold;
  line-height: 30px;
  color: #333333;
  ${Media.lessThan(Media.small)} {
    font-size: ${mbSize(32)};
    line-height: ${mbSize(45)};
  }
`

const CardText = styled.div`
  font-size: 14px;
  line-height: 24px;
  color: #333333;
  margin-top: 12px;
  ${Media.lessThan(Media.small)} {
    font-size: ${mbSize(28)};
    line-height: ${mbSize(48)};
    margin-top: ${mbSize(24)};
  }
`

const Button = styled.a<{ $variant?: 'primary' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 13px;
  height: 34px;
  border-radius: 4px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  transition: all 0.3s;
  cursor: pointer;
  min-width: max-content;

  ${({ $variant }) =>
    $variant === 'primary'
      ? `
    background-color: ${color.primary};
    color: ${color.white};
    border: none;
    
    &:hover {
      background-color: ${color.primaryDark};
      box-shadow: 0px 2px 3px 0px rgba(188, 144, 133, 0.08);
    }
  `
      : `
    background-color: ${color.white};
    color: ${color.textDark};
    border: 1px solid ${color.grey};
    
    &:hover {
      border-color: ${color.primary};
      color: ${color.primary};
      box-shadow: 0px 2px 3px 0px rgba(188, 144, 133, 0.08);
    }
  `}
`

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
  ${Button} {
    &:first-child {
      margin-bottom: 10px;
    }
  }
  ${Media.lessThan(Media.small)} {
    margin-top: ${mbSize(32)};
    justify-content: space-between;
    ${Button} {
      &:first-child {
        margin-bottom: ${mbSize(20)};
      }
    }
  }
`

const CardImage = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 230px;
  height: 212px;
  border-radius: 8px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: unset;
  }

  ${Media.lessThan(Media.small)} {
    width: 100%;
    height: ${mbSize(318)};
  }
`

export interface CardProps {
  title: string
  content: string
  button1Text?: string
  button1Link?: string
  button2Text?: string
  button2Link?: string
  imageLink?: string
  mobileImageLink?: string
  titleAttributes?: Record<string, string>
  contentAttributes?: Record<string, string>
  button1LinkAttributes?: Record<string, string>
  button2LinkAttributes?: Record<string, string>
  imageLinkAttributes?: Record<string, string>
}

export const Card: React.FC<CardProps> = ({
  title,
  content,
  button1Text,
  button1Link,
  button2Text,
  button2Link,
  imageLink,
  mobileImageLink,
  titleAttributes,
  contentAttributes,
  button1LinkAttributes,
  button2LinkAttributes,
  imageLinkAttributes,
  ...props
}) => {
  return (
    <CardContainer {...props}>
      <CardContent>
        <CardTitle {...titleAttributes} dangerouslySetInnerHTML={{ __html: title }} />
        <CardText {...contentAttributes} dangerouslySetInnerHTML={{ __html: content }} />
        {(button1Text || button2Text) && (
          <ButtonGroup>
            {button1Text && (
              <Button
                href={button1Link}
                target='_blank'
                rel='noreferrer nofollow'
                $variant='primary'
                {...button1LinkAttributes}
              >
                {button1Text}
              </Button>
            )}
            {button2Text && (
              <Button
                href={button2Link}
                target='_blank'
                rel='noreferrer nofollow'
                $variant='secondary'
                {...button2LinkAttributes}
              >
                {button2Text}
              </Button>
            )}
          </ButtonGroup>
        )}
      </CardContent>
      {imageLink && (
        <CardImage>
          <Img
            origin
            src={imageLink}
            alt={title}
            mbSrc={mobileImageLink}
            {...imageLinkAttributes}
          />
        </CardImage>
      )}
    </CardContainer>
  )
}
